import React, { useState, useEffect, useRef } from 'react';
import { Globe, Check, Star, CreditCard, GraduationCap, Users, Layout } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const EditorialEngageSection: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isVisible, setIsVisible] = useState(false);
  const [stats, setStats] = useState({ visitors: 0, conversion: 0, revenue: 0 });
  const [showFeatures, setShowFeatures] = useState([false, false, false]);
  const [reviewCount, setReviewCount] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const features = [
    { text: "Website & Funnel Builder & Hosting", icon: Layout },
    { text: "5-Star Review Automation", icon: Star },
    { text: "Integrated E-commerce & Billing", icon: CreditCard },
    { text: "Create & Sell Courses", icon: GraduationCap },
    { text: "Build & Monetize Communities", icon: Users }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    // Animate stats
    const duration = 1500;
    const steps = 30;
    let step = 0;
    
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setStats({
        visitors: Math.round(12400 * progress),
        conversion: Number((8.2 * progress).toFixed(1)),
        revenue: Math.round(48 * progress)
      });
      if (step >= steps) clearInterval(timer);
    }, duration / steps);

    // Animate review count
    const reviewTimer = setInterval(() => {
      setReviewCount(prev => {
        if (prev >= 247) return 247;
        return prev + 5;
      });
    }, 30);

    // Show features staggered
    setTimeout(() => setShowFeatures([true, false, false]), 600);
    setTimeout(() => setShowFeatures([true, true, false]), 900);
    setTimeout(() => setShowFeatures([true, true, true]), 1200);

    return () => {
      clearInterval(timer);
      clearInterval(reviewTimer);
    };
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-16 px-[7%] sm:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Visual - Website Builder */}
          <div className="order-2 lg:order-1">
            <div 
              className="rounded-xl p-6 overflow-hidden"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#F8F8F8', 
                border: isDark ? '1px solid #333333' : '1px solid #E5E5E5',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(-4px) scale(1)' : 'translateY(20px) scale(0.95)',
                boxShadow: isVisible ? (isDark ? '0 20px 40px -10px rgba(0, 0, 0, 0.5)' : '0 20px 40px -10px rgba(0, 0, 0, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.1)') : 'none',
                transition: 'all 0.6s ease-out'
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4" style={{ color: isDark ? '#AAAAAA' : '#555555' }} />
                  <span className="text-sm font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>Website Builder</span>
                </div>
                <div className="flex items-center gap-2">
                  <span 
                    className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${isDark ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}
                    style={{ animation: isVisible ? 'pulse 2s ease-in-out infinite' : 'none' }}
                  >
                    Published
                  </span>
                </div>
              </div>

              {/* Website Preview */}
              <div 
                className="rounded-lg overflow-hidden mb-4"
                style={{ backgroundColor: isDark ? '#222222' : '#FFFFFF', border: isDark ? '1px solid #333333' : '1px solid #E0E0E0' }}
              >
                {/* Browser bar */}
                <div className="flex items-center gap-1.5 px-3 py-2 border-b" style={{ borderColor: isDark ? '#333333' : '#E5E5E5', backgroundColor: isDark ? '#1A1A1A' : '#FAFAFA' }}>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-red-400" />
                    <div className="w-2 h-2 rounded-full bg-yellow-400" />
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 mx-2">
                    <div 
                      className="px-2 py-0.5 rounded text-[8px]"
                      style={{ backgroundColor: isDark ? '#333333' : '#F0F0F0', color: isDark ? '#888888' : '#666666' }}
                    >
                      yourbrand.com
                    </div>
                  </div>
                </div>
                
                {/* Website content */}
                <div className="p-3">
                  {/* Hero section */}
                  <div 
                    className="rounded-lg p-4 mb-3 text-center relative overflow-hidden" 
                    style={{ background: isDark ? 'linear-gradient(135deg, #3B82F630 0%, #8B5CF630 100%)' : 'linear-gradient(135deg, #3B82F620 0%, #8B5CF620 100%)' }}
                  >
                    {/* Shimmer */}
                    <div 
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: isDark 
                          ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                          : 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                        animation: isVisible ? 'shimmer 2s ease-in-out infinite' : 'none'
                      }}
                    />
                    <div 
                      className="text-xs font-bold mb-1 relative z-10"
                      style={{
                        color: isDark ? '#FFFFFF' : '#111111',
                        opacity: isVisible ? 1 : 0,
                        transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
                        transition: 'all 0.5s ease-out 0.3s'
                      }}
                    >
                      Transform Your Business
                    </div>
                    <div className="text-[8px] mb-2 relative z-10" style={{ color: isDark ? '#AAAAAA' : '#666666' }}>The all-in-one platform for growth</div>
                    <div 
                      className="inline-flex px-2 py-1 text-[7px] rounded relative z-10"
                      style={{
                        backgroundColor: isDark ? '#FFFFFF' : '#000000',
                        color: isDark ? '#000000' : '#FFFFFF',
                        animation: isVisible ? 'pulseGlow 2s ease-in-out infinite' : 'none'
                      }}
                    >
                      Get Started Free
                    </div>
                  </div>
                  
                  {/* Features grid */}
                  <div className="grid grid-cols-3 gap-2">
                    {['Funnels', 'Courses', 'Community'].map((item, i) => (
                      <div 
                        key={i} 
                        className="rounded p-2 text-center" 
                        style={{ 
                          backgroundColor: isDark ? '#333333' : '#F5F5F5',
                          opacity: showFeatures[i] ? 1 : 0,
                          transform: showFeatures[i] ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.9)',
                          transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                        }}
                      >
                        <div className="text-[8px] font-medium" style={{ color: isDark ? '#CCCCCC' : '#333333' }}>{item}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Stats Row with animated counters */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div 
                  className="rounded-lg p-2 text-center" 
                  style={{ backgroundColor: isDark ? '#222222' : '#FFFFFF', border: isDark ? '1px solid #333333' : '1px solid #E0E0E0' }}
                >
                  <div className="text-lg font-bold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
                    {stats.visitors >= 1000 ? `${(stats.visitors / 1000).toFixed(1)}K` : stats.visitors}
                  </div>
                  <div className="text-[9px]" style={{ color: isDark ? '#888888' : '#666666' }}>Visitors</div>
                </div>
                <div 
                  className="rounded-lg p-2 text-center" 
                  style={{ 
                    backgroundColor: isDark ? '#222222' : '#FFFFFF', 
                    border: isDark ? '1px solid #333333' : '1px solid #E0E0E0',
                    boxShadow: isVisible ? '0 0 15px rgba(16, 185, 129, 0.2)' : 'none',
                    transition: 'box-shadow 0.5s ease-out'
                  }}
                >
                  <div className={`text-lg font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{stats.conversion}%</div>
                  <div className="text-[9px]" style={{ color: isDark ? '#888888' : '#666666' }}>Conversion</div>
                </div>
                <div 
                  className="rounded-lg p-2 text-center" 
                  style={{ backgroundColor: isDark ? '#222222' : '#FFFFFF', border: isDark ? '1px solid #333333' : '1px solid #E0E0E0' }}
                >
                  <div className="text-lg font-bold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>${stats.revenue}K</div>
                  <div className="text-[9px]" style={{ color: isDark ? '#888888' : '#666666' }}>Revenue</div>
                </div>
              </div>

              {/* Review Widget */}
              <div 
                className="p-3 rounded-lg" 
                style={{ 
                  backgroundColor: isDark ? '#422006' : '#FEF3C7', 
                  border: isDark ? '1px solid #854d0e' : '1px solid #FCD34D',
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'all 0.5s ease-out 1s'
                }}
              >
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[1,2,3,4,5].map((star) => (
                      <Star 
                        key={star} 
                        className="w-3 h-3" 
                        style={{ 
                          fill: '#F59E0B', 
                          color: '#F59E0B',
                          animation: isVisible ? `starPop 0.3s ease-out ${star * 100 + 1000}ms both` : 'none'
                        }} 
                      />
                    ))}
                  </div>
                  <span className={`text-xs font-medium ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                    {reviewCount} reviews collected this month
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content */}
          <div className="order-1 lg:order-2">
            {/* Badge with animated border */}
            <div className="relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full overflow-hidden mb-6">
              <div 
                className="absolute inset-0 rounded-full"
                style={{
                  background: isDark 
                    ? 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(255,255,255,0.6) 50%, transparent 60%, transparent 100%)'
                    : 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(0,0,0,0.6) 50%, transparent 60%, transparent 100%)',
                  backgroundSize: '200% 100%',
                  animation: 'borderTrace 2s linear infinite',
                }}
              />
              <div 
                className="absolute inset-0 rounded-full"
                style={{ border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)' }}
              />
              <div 
                className="absolute inset-[1px] rounded-full"
                style={{ backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF' }}
              />
              <Globe className="relative z-10 w-3 h-3" style={{ color: isDark ? '#AAAAAA' : '#555555' }} />
              <span className="relative z-10 text-xs font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>Build</span>
            </div>

            <h2 
              className="text-3xl lg:text-4xl font-bold tracking-tight mb-4"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              WOW Your Window Shoppers.
            </h2>

            <p 
              className="text-sm mb-8"
              style={{ color: isDark ? '#AAAAAA' : '#555555' }}
            >
              Consolidate your tools, look professional, and build credibility faster than ever before.
            </p>

            {/* Feature checklist */}
            <div className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.5s ease-out ${index * 100}ms`
                  }}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                    <Check className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  </div>
                  <span className="text-sm" style={{ color: isDark ? '#CCCCCC' : '#333333' }}>{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <button 
              onClick={() => document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98] hover:opacity-90"
              style={{
                backgroundColor: isDark ? '#FFFFFF' : '#000000',
                color: isDark ? '#000000' : '#FFFFFF',
              }}
            >
              Start Building →
            </button>
          </div>
        </div>
      </div>

    </section>
  );
};

export default EditorialEngageSection;
